import type { useGridApiRef } from "@mui/x-data-grid";
import { useCallback, useEffect, useRef } from "react";
import type { AnalyticValues } from "../../contexts/analytics/hooks";
import type { NurseData } from "../../utils/models";
import { NURSES_MATRIX_ANALYTICS_SOURCE, type NursesMatrixSelectionMode } from "./consts";

type TrackFn = (event: string, props?: AnalyticValues) => void;
type GridApiRef = ReturnType<typeof useGridApiRef>;

interface UseNursesMatrixKeyboardNavArgs {
	/** All rows currently handed to the grid (unsorted is fine — display order is read from the grid apiRef). */
	rows: NurseData[];
	/** The grid's apiRef — used to read the current display order and to scroll the cursor row into view. */
	apiRef: GridApiRef;
	/** Row identity (matches the grid's `getRowId`). NOT the nurse id when duplicates share a nurse. */
	getRowId: (row: NurseData) => string | number;
	cursorRowId: string | number | null;
	setCursorRowId: (id: string | number | null) => void;
	selectedRowId: string | number | null;
	setSelectedRowId: (id: string | number | null) => void;
	/** Called with the committed row, or `null` when a re-commit clears the selection (deselectOnReselect). */
	onSelectNurse?: (nurse: NurseData | null) => void;
	/** When true, committing the already-selected row clears the selection (toggle) instead of re-committing. */
	deselectOnReselect?: boolean;
	/** Nurses queued for deny this session — cannot be committed, matching the row-click guard. */
	queuedDenyIdSet?: Set<number>;
	/** Off switch (default true). Lets read-only / multi-instance consumers avoid attaching a second listener. */
	enabled?: boolean;
	/** Analytics tracker from the grid's `useAnalytics()` (avoids the deprecated module-level `trackEvent`). */
	track: TrackFn;
	/** `data-testid` of the grid's own search box, so arrows still drive the picker while it's focused. */
	searchTestId?: string;
	/**
	 * Controlled picking (the grid's `selectionMode`). When set, Enter hands the cursor row to `onPickRow`
	 * instead of the internal `selectedRowId`, and — in `"multiple"` only — Space does too. Absent = the
	 * legacy Enter-commits picker, untouched.
	 */
	selectionMode?: NursesMatrixSelectionMode;
	/** Picks the row in the controlled modes: selects it (single) or toggles it (multiple). */
	onPickRow?: (row: NurseData) => void;
}

const NAV_KEYS = new Set(["ArrowDown", "ArrowUp", "Enter", " "]);

/** Elements where Space means "type a space" or "press me" — never "toggle the cursor row". */
const isSpaceOwner = (el: Element | null): boolean =>
	el instanceof HTMLElement &&
	(el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT", "BUTTON", "A"].includes(el.tagName) || el.getAttribute("role") === "button");

/**
 * In the controlled modes a pick moves the cursor, so a stale cursor would otherwise turn Enter on any
 * focused control ("Add 3 to shift", a shift card, a detail chip) into "un-pick that nurse". Enter
 * belongs to the control that holds focus — except the grid's own search box, where Enter has no
 * native meaning and picking the cursor row is the keyboard flow.
 */
const isEnterOwner = (el: Element | null, searchTestId: string | undefined): boolean => {
	if (!isSpaceOwner(el) || !(el instanceof HTMLElement)) return false;
	return !(searchTestId && el.closest(`[data-testid="${searchTestId}"]`));
};

/** A modal that is mounted but closed (`keepMounted`): MUI hides its root rather than unmounting it. */
const isHiddenModal = (dialog: Element): boolean =>
	!!dialog.closest('[aria-hidden="true"], [hidden], .MuiModal-hidden') || getComputedStyle(dialog).visibility === "hidden";

/**
 * A MODAL opened ON TOP of the grid (the multi-add review, a warning confirm) owns Enter and Space. Only
 * visible `aria-modal` dialogs count (MUI's Dialog paper carries it): a non-modal `role="dialog"` overlay —
 * the Annie chat window floating over every page, a notice popover — must not switch the picker off, and
 * neither must a closed `keepMounted` dialog. Modals that CONTAIN the grid (the Recommendations popup
 * hosting the picker) don't count either. Before the grid root mounts every open modal counts as foreign.
 */
function isForeignDialogOpen(apiRef: GridApiRef): boolean {
	const gridRoot = apiRef.current?.rootElementRef?.current;
	return Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]')).some(
		(dialog) => !isHiddenModal(dialog) && (!gridRoot || !dialog.contains(gridRoot)),
	);
}

/**
 * True when an unrelated text control (TimeField segments, a message textarea) holds focus — its native
 * Arrow/Enter must win. The grid's own search box is exempt so arrows keep driving the picker while the
 * user types (legacy behavior).
 */
function isForeignInputFocused(searchTestId: string | undefined): boolean {
	const active = document.activeElement;
	if (!(active instanceof HTMLElement)) return false;
	if (active.tagName !== "INPUT" && active.tagName !== "TEXTAREA" && !active.isContentEditable) return false;
	return !(searchTestId && active.closest(`[data-testid="${searchTestId}"]`));
}

/** The grid's rows in on-screen order (grid-owned once mounted; prop order before the first mount). */
function getOrderedRows(rows: NurseData[], getRowId: (row: NurseData) => string | number, apiRef: GridApiRef): NurseData[] {
	const rowById = new Map(rows.map((r) => [getRowId(r), r]));
	const api = apiRef.current;
	const orderedIds = api?.getSortedRowIds ? api.getSortedRowIds() : rows.map((r) => getRowId(r));
	return orderedIds.map((id) => rowById.get(id)).filter((r): r is NurseData => r != null);
}

/**
 * Keyboard picker for the nurses matrix grid, reimplemented on top of `SystemDataGrid`.
 *
 * Arrow up/down move a visual `cursorRowId` over the SELECTABLE rows in their current on-screen order
 * (read from the grid apiRef so a re-sort never strands the cursor, and a folded group band's rows are
 * skipped because they are not on screen); Enter commits the cursor as the selected row and fires
 * `onSelectNurse` + analytics. In the controlled selection modes Enter (and Space, multiple only) hand
 * the cursor row to `onPickRow` instead. The cursor row is scrolled into view via
 * `apiRef.current.scrollToIndexes({ rowIndex })` so it stays visible even when virtualized off-screen.
 *
 * The listener is WINDOW-scoped (not grid-scoped) so arrows keep navigating while the grid's OWN search
 * box is focused (legacy behavior). It bails when an unrelated input/textarea/contentEditable is focused
 * (e.g. a MUI TimeField's spin segments, a message field) so it never steals those controls' native
 * Arrow/Enter handling, and it bails when a native select/menu is open. Space never fires from a text
 * field — the search box included, where it types a space — nor from a focused button. In the controlled
 * modes Enter is likewise left to a focused button/link/field (the search box excepted), and both Enter and
 * Space are left to a modal dialog opened over the grid. Arrows are captured (capture phase + stopPropagation) so the DataGrid's own cell
 * arrow navigation doesn't also fire (no double-move).
 */
export function useNursesMatrixKeyboardNav(args: UseNursesMatrixKeyboardNavArgs): void {
	const { apiRef, enabled = true } = args;
	// Bundle the changing values behind a ref so the listener can attach once and stay stable while
	// always reading current values.
	const latest = useRef(args);
	latest.current = args;

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (!NAV_KEYS.has(e.key)) return;
			const current = latest.current;
			if (e.key === " " && (current.selectionMode !== "multiple" || isSpaceOwner(document.activeElement))) return;
			// Legacy (no selectionMode) keeps its window-wide Enter commit untouched.
			if (e.key === "Enter" && current.selectionMode && isEnterOwner(document.activeElement, current.searchTestId)) return;
			// A modal over the grid owns both picking keys — e.g. the review dialog, whose focused container
			// is no Space owner, must not let Space un-pick the nurse behind it.
			if ((e.key === "Enter" || e.key === " ") && current.selectionMode && isForeignDialogOpen(apiRef)) return;
			// An open dropdown/menu owns arrows + Enter for option navigation — don't hijack them.
			if (document.querySelector('[role="listbox"], [role="menu"]')) return;
			if (isForeignInputFocused(current.searchTestId)) return;

			const orderedRows = getOrderedRows(current.rows, current.getRowId, apiRef);
			const selectableRows = orderedRows.filter((r) => r.selectable !== false);
			if (selectableRows.length === 0) return;
			const currentIndex = current.cursorRowId == null ? -1 : selectableRows.findIndex((n) => current.getRowId(n) === current.cursorRowId);

			if (e.key === "Enter" || e.key === " ") {
				// Only swallow the key when there's a cursor to act on — otherwise let unrelated buttons/forms act.
				if (currentIndex < 0) return;
				e.preventDefault();
				// Space would otherwise also reach the grid's own cell handlers (and scroll the page).
				if (e.key === " ") e.stopPropagation();
				activateRow(selectableRows[currentIndex], current);
				return;
			}
			moveCursor(e, { orderedRows, selectableRows, currentIndex, current, apiRef });
		},
		[apiRef],
	);

	useEffect(() => {
		if (!enabled) return;
		// Capture phase: the window handler runs before the DataGrid's cell handlers, so stopPropagation
		// on arrows prevents the grid from also moving cell focus. Window-scoped on purpose (see JSDoc).
		window.addEventListener("keydown", handleKeyDown, true);
		return () => window.removeEventListener("keydown", handleKeyDown, true);
	}, [handleKeyDown, enabled]);
}

/** Enter/Space on the cursor row: pick it (controlled modes) or commit it as the legacy selection. */
function activateRow(nurse: NurseData, current: UseNursesMatrixKeyboardNavArgs): void {
	// Match the row-click guard: never commit a denied nurse (queued this session or server-denied).
	const isDenied = current.queuedDenyIdSet?.has(nurse.id) || nurse.isDenied === true;
	if (isDenied) return;
	if (current.selectionMode) {
		current.onPickRow?.(nurse);
		current.track("staffing_matrix_keyboard_select", { source: NURSES_MATRIX_ANALYTICS_SOURCE, nurse_id: nurse.id, selection_mode: current.selectionMode });
		return;
	}
	const id = current.getRowId(nurse);
	if (current.deselectOnReselect && id === current.selectedRowId) {
		current.setSelectedRowId(null);
		current.setCursorRowId(null);
		current.onSelectNurse?.(null);
		return;
	}
	current.setSelectedRowId(id);
	current.onSelectNurse?.(nurse);
	current.track("staffing_matrix_keyboard_select", { source: NURSES_MATRIX_ANALYTICS_SOURCE, nurse_id: nurse.id });
}

interface MoveCursorContext {
	orderedRows: NurseData[];
	selectableRows: NurseData[];
	currentIndex: number;
	current: UseNursesMatrixKeyboardNavArgs;
	apiRef: GridApiRef;
}

/** Arrow: move the cursor and suppress the grid's native cell navigation (capture + stopPropagation). */
function moveCursor(e: KeyboardEvent, { orderedRows, selectableRows, currentIndex, current, apiRef }: MoveCursorContext): void {
	e.preventDefault();
	e.stopPropagation();
	const direction = e.key === "ArrowDown" ? "down" : "up";
	const newIndex =
		direction === "down" ? (currentIndex < 0 ? 0 : Math.min(currentIndex + 1, selectableRows.length - 1)) : currentIndex <= 0 ? 0 : currentIndex - 1;
	const target = selectableRows[newIndex];
	const targetId = current.getRowId(target);
	current.setCursorRowId(targetId);
	current.track("staffing_matrix_keyboard_navigate", { source: NURSES_MATRIX_ANALYTICS_SOURCE, direction, nurse_id: target.id });
	// Scroll using the cursor's index in the FULL displayed order (matches virtualization indexes).
	const rowIndex = orderedRows.findIndex((r) => current.getRowId(r) === targetId);
	if (rowIndex >= 0) apiRef.current?.scrollToIndexes?.({ rowIndex });
}
